import {
  requestComment,
  requestDonation,
  requestShare,
} from '@src/interfaces/community';
import contentMock from '@src/services/communityMock';
import { requestAPI } from '@src/utils/fetch';

const getDonations = async (
  page: number,
  keyword?: string,
  abo?: string,
  bloodProduct?: string,
  sortBy?: string,
  region?: string,
  urgent?: boolean
) => {
  const { data } = await requestAPI().get(
    `/api/v3/donation/search?page=${page + 1}&keyword=${keyword || ''}&abo=${
      abo || ''
    }&bloodProduct=${bloodProduct || ''}&sortBy=${sortBy || ''}&region=${
      region || ''
    }${urgent ? '&urgent=true' : ''}`
  );
  return data;
};

const getBoards = async (page: number, keyword?: string) => {
  // 빈 keyword 를 붙여 보내면 서버가 검색으로 취급해 캐시를 못 타므로 값이 있을 때만 넣는다.
  const query = keyword ? `&keyword=${encodeURIComponent(keyword)}` : '';
  const { data } = await requestAPI().get(
    `/api/v1/board/search?page=${page + 1}${query}`
  );
  return data;
};

const getBoard = async (id: string) => {
  const { data } = await requestAPI().get(`/api/v1/board/${id}`);
  return data;
};

const getDonation = async (id: string) => {
  const { data } = await requestAPI().get(`/api/v1/donation/${id}`);
  return data;
};

const toggleLike = async (id: string, communityType: string) => {
  if (communityType === 'donation') {
    const { data } = await requestAPI().post(`/api/v1/donation/like/${id}`);
    return data;
  } else {
    const { data } = await requestAPI().post(`/api/v1/board/like/${id}`);
    return data;
  }
};

const insertDonation = async (request: requestDonation) => {
  const data = await requestAPI().post(
    `/api/v2/donation`,
    request,
    'multipart/form-data'
  );
  return data;
};
const insertBoard = async (request: any) => {
  const data = await requestAPI().post(
    `/api/v1/board`,
    request,
    'multipart/form-data'
  );
  return data;
};

const updateDonation = async (request: requestDonation) => {
  const body = {
    bloodProduct: request.bloodProduct,
    content: request.content,
    patient: request.patient,
    title: request.title,
    phoneNumber: request.phoneNumber,
  };
  const data = await requestAPI().patch(`/api/v1/donation/${request.id}`, body);
  return data;
};

const updateBoard = async (request: any) => {
  const data = await requestAPI().patch(`/api/v1/board`, request);
  return data;
};

const insertComment = async (body: requestComment, communityType: string) => {
  if (communityType === 'donation') {
    const data = await requestAPI().post(
      'api/v1/donation/reply',
      body.requestData
    );
    return data;
  } else {
    const data = await requestAPI().post(
      'api/v1/board/reply',
      body.requestData
    );
    return data;
  }
};

const insertReplyComment = async (
  body: requestComment,
  communityType: string
) => {
  if (communityType === 'donation') {
    const data = await requestAPI().post(
      'api/v1/donation/reply/reply',
      body.requestData
    );
    return data;
  } else {
    const data = await requestAPI().post(
      'api/v1/board/reply/reply',
      body.requestData
    );
    return data;
  }
};

const deleteCommunity = async (id: string, communityType: string) => {
  if (communityType === 'donation') {
    const data = await requestAPI().delete(`/api/v1/donation/delete/${id}`);
    return data;
  } else {
    const data = await requestAPI().delete(`/api/v1/board/${id}`);
    return data;
  }
};

const updateReplyComment = async (
  body: requestComment,
  communityType: string
) => {
  if (communityType === 'donation') {
    const data = await requestAPI().patch(
      `/api/v1/donation/reply/reply`,
      body.requestData
    );
    return data;
  } else {
    const data = await requestAPI().patch(
      `/api/v1/donation/reply/reply`,
      body.requestData
    );
    return data;
  }
};

const updateComment = async (body: requestComment, communityType: string) => {
  if (communityType === 'donation') {
    const data = await requestAPI().patch(
      `/api/v1/donation/reply`,
      body.requestData
    );
    return data;
  } else {
    const data = await requestAPI().patch(
      `/api/v1/donation/reply`,
      body.requestData
    );
    return data;
  }
};

const deleteComment = async (id: string, communityType: string) => {
  if (communityType === 'donation') {
    const data = await requestAPI().delete(`/api/v1/donation/reply/${id}`);
    return data;
  } else {
    const data = await requestAPI().delete(`/api/v1/board/reply/${id}`);
    return data;
  }
};

const deleteReplyComment = async (id: string, communityType: string) => {
  if (communityType === 'donation') {
    const data = await requestAPI().delete(
      `/api/v1/donation/reply/reply/${id}`
    );
    return data;
  } else {
    const data = await requestAPI().delete(`/api/v1/board/reply/reply/${id}`);
    return data;
  }
};

const postShareDonation = async (body: requestShare) => {
  const { data } = await requestAPI().post(`/api/v1/donation/url`, body);
  return data;
};

const encodedGetDonation = async (id: string) => {
  const { data } = await requestAPI().get(`/api/v1/donation/detail/${id}`);
  return data;
};
const communityApiList = {
  getDonations,
  getBoards,
  getBoard,
  getDonation,
  toggleLike,
  insertDonation,
  insertBoard,
  insertComment,
  insertReplyComment,
  updateDonation,
  updateBoard,
  deleteCommunity,
  updateReplyComment,
  updateComment,
  deleteComment,
  deleteReplyComment,
  postShareDonation,
  encodedGetDonation,
};

/**
 * 디자인 확인용 더미 모드. .env.local의 NEXT_PUBLIC_CONTENT_MOCK=true
 * 운영 빌드(NODE_ENV=production)에서는 플래그가 켜져 있어도 절대 동작하지 않는다.
 */
export const isContentMock = () =>
  process.env.NODE_ENV !== 'production' &&
  process.env.NEXT_PUBLIC_CONTENT_MOCK === 'true';

// mock 모드면 목록/상세 조회만 더미로 대체한다(쓰기 동작은 실제 API 유지).
const communityApi: typeof communityApiList = new Proxy(communityApiList, {
  get(target, prop: string) {
    if (isContentMock() && prop in contentMock) {
      return (contentMock as any)[prop];
    }
    return (target as any)[prop];
  },
});

export default communityApi;
